import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import type {
  IGrid,
  IGridAction,
  IGridEvent,
  IGridLinkQueryParams,
  ISegment
} from '@care-portals/api-interfaces';
import { endOfDay, startOfDay } from 'date-fns';
import { NzTableQueryParams } from 'ng-zorro-antd/table';

const DEFAULT_PAGE_SIZE = 25;
@Component({
  selector: 'grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.less']
})
export class GridComponent<T> implements OnInit, OnDestroy {
  constructor(private fb: FormBuilder) { }

  private _showTable = true;
  get showTable(): boolean {
    return this._showTable;
  }
  @Input() set showTable(v: boolean) {
    this._showTable = v;
    // to force the table to update its UI and reflect changes
    this.data = [...this.data];
  }

  sorters = {
    text: (name: string) => (a: T, b: T) => a[name]?.localeCompare(b?.[name]),
    link: (name: string) => (a: T, b: T) => a[name]?.localeCompare(b?.[name]),
    status: (name: string) => (a: T, b: T) => a[name]?.localeCompare(b?.[name]),
    tags: (name: string) => (a: T, b: T) =>
      a[name]?.[0]?.localeCompare(b?.[name]?.[0]),
    date: (name: string) => (a: T, b: T) =>
      new Date(b?.[name]).getTime() - new Date(a?.[name]).getTime()
  };

  @Input() showContent: boolean;
  @Input() grid: IGrid<T>;
  @Input() data: T[];
  @Input() total: number;
  @Input() loading: boolean;
  @Input() ajax = false;
  @Input() urlParamsFilters = true;
  @Input() virtualScroll = true;
  @Input() virtualScrollHeight: string;
  @Input() showFilters: boolean;
  @Input() segmentData: ISegment;
  @Input() scrollX: string;
  /** function to validate rows
   * @returns {colKey: errorMessage}
   */
  @Input() validateFn: (record: T) => Record<string, string>;
  /** unmodified filters */
  @Input() fixedFilters: { [key: string]: any };
  @Input() customFilters: TemplateRef<any>;
  @Output() actionTriggered = new EventEmitter<IGridEvent<T>>();

  filterFG: FormGroup;
  recordActions: IGridAction<T>[];
  gridActions: IGridAction<T>[];
  setOfCheckedId = new Set<string | number>();
  allChecked = false;
  indeterminate = false;

  pageSize: number;
  pageIndex: number;
  segment: string;

  /** {recordIndex: {colName: errorMessage}} */
  validationErrors: Record<number, any> = {};

  private _filters: any; // applied filters
  public get filters(): { [key: string]: any } {
    return this._filters;
  }
  public set filters(value: { [key: string]: any }) {
    localStorage.setItem(`${this.grid.name}_filters`, JSON.stringify(value));

    const v = { ...value };
    const keys = Object.keys(v);

    // url queryParams
    const urlParams = new URLSearchParams(window.location.search);

    keys.forEach(key => {
      if (!v[key]) {
        this.urlParamsFilters && urlParams.delete(key);
        return;
      }

      const type = this.grid.cols.find(x => x.name === key)?.filter?.type;

      switch (type) {
        case 'date':
          if (!v[key]) {
            delete v[key];
            this.urlParamsFilters && urlParams.delete(key);
            return;
          }

          v[key] = startOfDay(v[key]);
          this.urlParamsFilters &&
            urlParams.set(key, (v[key] as Date).getTime().toString());
          return;

        case 'date-range':
          if (!v[key][0] || !v[key][1]) {
            delete v[key];
            this.urlParamsFilters && urlParams.delete(key);
            return;
          }

          v[key] = [startOfDay(v[key][0]), endOfDay(v[key][1])];
          this.urlParamsFilters &&
            urlParams.set(
              key,
              (v[key][0] as Date).getTime() +
              '_' +
              (v[key][1] as Date).getTime()
            );
          return;

        default:
          this.urlParamsFilters && urlParams.set(key, v[key]);
          return;
      }
    });

    const href =
      window.location.href.split('?')[0] + '?' + urlParams.toString();
    window.history.replaceState(null, null, href);

    this._filters = { ...v, ...this.fixedFilters };
  }

  ngOnInit(): void {
    if (!this.grid)
      throw new Error('GridComponent:: @Input() grid: IGrid<T> is required');

    if (this.showFilters === null || this.showFilters === undefined) {
      this.showFilters = this.grid.cols.some(x => !!x.filter);
    }

    this.recordActions = this.grid.actions?.filter(x => x.type === 'record');
    this.gridActions = this.grid.actions?.filter(x => x.type === 'grid');

    // virtualScroll
    if (!this.virtualScrollHeight) {
      this.virtualScrollHeight = this.grid.isPage
        ? window.innerHeight - 245 + 'px'
        : '500px';
    }

    const urlParams = new URLSearchParams(window.location.search);

    const filterFG: any = {};
    let savedFilters = {};
    try {
      savedFilters = JSON.parse(
        localStorage.getItem(`${this.grid.name}_filters`) || '{}'
      );
    } catch (error) {
      localStorage.removeItem(`${this.grid.name}_filters`);
      console.error('failed to parse saved filters');
    }

    this.grid.cols.forEach(col => {
      if (!col) return;
      if (col.filter) {
        // default value
        const name = (col.filter.name || col.name).toString();
        let v;
        switch (col.filter.type) {
          case 'date':
            v =
              (this.urlParamsFilters && urlParams.get(name)) ||
              savedFilters[name] ||
              col.filter.defaultValue ||
              null;
            if (v) v = Number.isNaN(Number(v)) ? new Date(v) : new Date(+v);
            break;

          case 'date-range':
            v =
              (this.urlParamsFilters && urlParams.get(name)?.split('_')) ||
              savedFilters[name] ||
              col.filter.defaultValue ||
              null;
            if (v)
              v = v.map(x =>
                Number.isNaN(Number(x)) ? new Date(x) : new Date(+x)
              );
            break;

          default:
            v =
              (this.urlParamsFilters && urlParams.get(name)) ||
              savedFilters[name] ||
              col.filter.defaultValue ||
              null;
            break;
        }

        filterFG[name] = [v];
      }
    });
    this.filterFG = this.fb.group(filterFG);
    this.applyFilters(false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.validateFn && changes.data) {
      this.validateRows();
    }
  }
  ngOnDestroy(): void { }

  emit(e: IGridEvent<T>) {
    this.actionTriggered.emit(e);
  }

  resolveQueryParams(
    source: { queryParams?: IGridLinkQueryParams<T> },
    record: T
  ): Record<string, string> | null {
    if (!source.queryParams) {
      return null;
    }

    const params =
      typeof source.queryParams === 'function'
        ? source.queryParams(record)
        : source.queryParams;

    return params && Object.keys(params).length ? params : null;
  }

  emitSelection() {
    this.emit({
      action: 'selection',
      payload: { ids: [...this.setOfCheckedId] }
    });
  }

  applyFilters(reset: boolean = true) {
    this.filters = this.filterFG.value;

    const urlParams = new URLSearchParams(window.location.search);
    if (this.ajax) {
      this.pageSize = +urlParams.get('size') || DEFAULT_PAGE_SIZE;
      this.pageIndex = reset ? 1 : (+urlParams.get('page') || 1);

      this.filters = {
        ...this.filterFG.value,
        page: this.pageIndex,
        size: this.pageSize
      };
    } else {
      this.filters = {
        ...this.filterFG.value
      };
    }

    this.segment = urlParams.get('segment');
    if (this.segment) {
      this.filters.segment = this.segment;
      // tell the parent component to fetch segment data and pass it to the grid (@Input() segmentData) to display the segment (name/description)
      this.emit({ action: 'segment', payload: { id: this.segment } });
    }

    this.load();
  }

  resetFilters() {
    this.filterFG.reset();
    this.applyFilters();
  }

  clearSegment() {
    this.segmentData = null;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('segment')) {
      urlParams.delete('segment');
      window.history.replaceState(
        {},
        '',
        window.location.pathname + '?' + urlParams.toString()
      );
    }
    this.applyFilters();
  }

  load() {
    this.emit({ action: 'load', payload: this.filters });
  }

  onItemChecked(id: string, checked: boolean): void {
    this.updateCheckedSet(id, checked);
    this.refreshAllCheckedStatus();
  }

  refreshAllCheckedStatus(): void {
    this.allChecked = this.data.every(({ _id }: any, i) =>
      this.setOfCheckedId.has(_id || i)
    );
    this.indeterminate =
      this.data.some(({ _id }: any, i) => this.setOfCheckedId.has(_id || i)) &&
      !this.allChecked;
  }

  updateCheckedSet(id: string, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }

  clearSelection() {
    this.onAllChecked(false);
  }

  onAllChecked(checked: boolean) {
    const enableInvalidSelection = false;
    this.data.forEach(({ _id }: any, i) =>
      this.updateCheckedSet(
        _id || i,
        (enableInvalidSelection || !this.validationErrors[i]) && checked
      )
    );
    this.refreshAllCheckedStatus();
  }

  validateRows() {
    if (!this.validateFn || !this.data) {
      this.validationErrors = {};
      return;
    }

    const _validationError = {};

    this.data.forEach((record, i) => {
      const errors = this.validateFn(record);

      const errColsKeys = Object.keys(errors || {});
      if (errColsKeys.length === 0) {
        return;
      }

      const recordErrors: Record<string, string> = {};
      for (const col of errColsKeys) {
        recordErrors[col] = errors[col];
      }

      _validationError[i] = recordErrors;
    });

    this.validationErrors = _validationError;

    // auto select all valid records
    this.onAllChecked(true);
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageIndex, pageSize, sort } = params;
    const currentSort = sort.find(item => item.value !== null);
    const sortField = (currentSort && currentSort.key) || null;
    const sortOrder = (currentSort && currentSort.value) || null;
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;

    this.filters = {
      ...this.filters,
      page: pageIndex,
      size: pageSize,
      orderBy: sortField,
      orderDir: sortOrder
    };
    this.load();
  }
}
